import { ComponentType, useContext, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { includes } from 'lodash';

import { CognitoAuthContext } from 'features/auth';
import { EnumUserRole } from '../graphql';
import { moduleRegistry } from 'lib/modules';
import { useUserState } from 'providers/user-state-provider';
import { Error, FullscreenLoading } from '@farmshare/ui-components';
import logo from 'assets/images/partners.svg';

interface ModuleProtectedRouteProps {
  component: ComponentType;
  moduleId: string;
  allowedUserRoles?: EnumUserRole[];
  fallbackPath?: string;
  skipLazyWrapper?: boolean;
}

/**
 * Protected route component that checks both authentication and module access
 * @param root0
 * @param root0.component
 * @param root0.moduleId
 * @param root0.allowedUserRoles
 * @param root0.fallbackPath
 * @param root0.skipLazyWrapper
 */
export function ModuleProtectedRoute({
  component,
  moduleId,
  allowedUserRoles = [],
  fallbackPath = '/',
  skipLazyWrapper = false,
}: ModuleProtectedRouteProps) {
  const { user } = useUserState();
  const cognitoAuth = useContext(CognitoAuthContext);
  const location = useLocation();

  // Load active modules when user or modules change
  useEffect(() => {
    if (user?.active_vendor?.modules && user?.role) {
      moduleRegistry.loadActiveModules(user.active_vendor.modules, user.role);
    }
  }, [user?.active_vendor?.modules, user?.role]);

  // Show loading while checking authentication
  if (cognitoAuth?.isLoading) {
    return <FullscreenLoading />;
  }

  // If not authenticated, redirect to login
  if (!cognitoAuth?.isAuthenticated) {
    const redirectPath = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(redirectPath)}`}
        replace
      />
    );
  }

  // Only check module status if we've already loaded modules
  // This prevents redirecting before the useEffect above runs
  const hasLoadedModules = moduleRegistry.getActiveModules().length > 0;
  const isModuleActive = moduleRegistry.isModuleActive(moduleId);

  if (hasLoadedModules && !isModuleActive) {
    return <Navigate to={fallbackPath} replace />;
  }

  const Component = component;

  // Ensure that VendorOwners can access Vendor routes
  const effectiveAllowedRoles = [...allowedUserRoles];
  if (
    user?.role === EnumUserRole.VendorOwner &&
    allowedUserRoles.includes(EnumUserRole.Vendor)
  ) {
    effectiveAllowedRoles.push(EnumUserRole.VendorOwner);
  }

  // Check user role
  if (
    user &&
    effectiveAllowedRoles.length > 0 &&
    !includes(effectiveAllowedRoles, user.role) &&
    user.role !== EnumUserRole.Admin
  ) {
    return (
      <Error
        error={{ name: '403', message: 'Access denied.' }}
        logo={
          <img
            src={logo}
            height={100}
            alt="farmshare partners logo"
            className="mt-1 text-body p-3 p-md-0"
          />
        }
      />
    );
  }

  if (skipLazyWrapper) {
    return <Component />;
  }

  return <Component />;
}
